import { logger } from '../utils/logger';
import { authService } from './authService';
import { ReauthTurn, SignInExpiredError, SIGN_IN_EXPIRED_MESSAGE, fetchWithReauth } from './reauth';
import { graphService } from './graphService';
import type { Email } from './graphService';
import type {
  ActiveContext,
  DocumentContextChunk,
  IntentType,
  SuggestedApp,
  ThreadSummaryEntry,
} from '../types';
// Re-exported so existing importers of this module keep working; the canonical
// definition lives in ../types alongside Message.
export type { SuggestedApp };
import { getAccessibleAppKeys, getAccessibleApp } from '../config/appRegistry';
import { useNewIntentRouting, getChatDeployment, getIntentDeployment, usePropertyCorpus } from '../utils/constants';
import { queryLooksLikeDocumentComparison } from '../utils/documentChunks';
import { buildDocumentFollowUpClarification } from '../utils/documentFollowUp';
import { buildStructuredSpreadsheetResponse } from '../utils/spreadsheetStructuredResponses';
import { queryHasExplicitDate, parseDateRange, buildTemporalContext } from './dateParser';

// Azure Function endpoints for grounding
const resolveGroundingApiBase = () => {
  if (import.meta.env.VITE_GROUNDING_API_BASE) {
    return import.meta.env.VITE_GROUNDING_API_BASE;
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    if (hostname === 'rrrealty.ai' || hostname === 'www.rrrealty.ai') {
      return 'https://fn-conversationsave.azurewebsites.net/api';
    }

    if (hostname === 'testing.rrrealty.ai') {
      return 'https://fn-conversationsave.azurewebsites.net/api';
    }

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:7071/api';
    }
  }

  return 'https://fn-conversationsave.azurewebsites.net/api';
};

const GROUNDING_API_BASE = resolveGroundingApiBase();

/**
 * Content-Type plus a bearer token when one can be had, silent-only: this never
 * opens a login popup, whoever the caller is (background logging and the
 * user-initiated classifier and grounding calls alike). Token acquisition
 * failing (nobody signed in, MSAL hiccup) must never break the user's chat or
 * fire-and-forget logging, so the request goes out without the header instead.
 * A user-initiated call that then gets a 401 is recovered by fetchWithReauth
 * (one silent forced refresh) and, failing that, by the "Sign in again" button;
 * never by a prompt from here.
 */
async function buildJsonHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const accessToken = await authService.getAccessTokenSilent();
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  } catch (error) {
    logger.debug('No access token for backend call; sending without Authorization:', error);
  }
  return headers;
}

export interface Citation {
  id: number;
  title: string;
  url: string;
  snippet?: string;
}

export interface GroundingResponse {
  success: boolean;
  answer: string;
  citations: Citation[];
  groundingSource: string;
  queryId?: string;
  error?: string;
  threadSummaries?: ThreadSummaryEntry[];
  suggestedApp?: SuggestedApp | null;
  /** The turn ended on an unrecoverable 401: the UI offers "Sign in again". */
  requiresSignIn?: boolean;
  metadata?: {
    filterCriteria?: any;
    resultCount?: number;
    totalCount?: number;
  };
}

/**
 * Final client-side gate on an app suggestion.
 *
 * The backend already restricted the classifier to the caller's permitted apps
 * and dropped anything outside that set. This re-checks the same claim against
 * the token as it stands right now, so a stale or tampered response cannot put
 * an inaccessible app in front of the user. Cheap, and it keeps the rule in one
 * place the UI can rely on: if this returns null, nothing is rendered.
 *
 * Uses the registry's URL, never the URL the backend sent, so a compromised or
 * injected response cannot turn a handoff card into an arbitrary outbound link.
 */
function sanitizeSuggestedApp(raw: unknown): SuggestedApp | null {
  if (!raw || typeof raw !== 'object') return null;
  const key = (raw as { key?: unknown }).key;
  if (typeof key !== 'string') return null;

  const entry = getAccessibleApp(key);
  if (!entry) return null;

  return {
    key: entry.key,
    name: entry.label,
    url: entry.url,
    summary: entry.blurb,
  };
}

export interface QueryClassification {
  intent?: IntentType;
  suggestedApp?: SuggestedApp | null;
  groundingType: 'real_time_search' | 'enterprise_knowledge' | 'general_knowledge' | 'email_query' | 'calendar_query' | 'scheduling_query' | 'property_query' | 'none';
  confidence: number;
  reasons: string[];
  entities?: {
    people?: string[];
    dates?: {
      start?: string;
      end?: string;
      relative?: string;
    };
    keywords?: string[];
    actions?: string[];
    kql?: string | null;
    duration?: number;
    attendees?: string[];
  };
  context?: string;
  ossClassification?: any;
  responseTime?: number;
}

interface ParsedGroundingResult {
  answer: string;
  citations: Citation[];
  groundingSource: string;
  backendError?: string;
}

interface EmailFilterCriteria {
  from?: string;
  to?: string;
  subject?: string;
  startDate?: Date;
  endDate?: Date;
  isUnread?: boolean;
  searchText?: string;
}

interface EmailThreadSummary {
  id: string;
  subject: string;
  participants: string[];
  messageCount: number;
  lastMessageDate: Date;
  summary: string;
  keyPoints?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
  requiresAction?: boolean;
}

class GroundingService {
  private parseGroundingResult(result: any): ParsedGroundingResult {
    const groundingSource =
      result?.groundingSource ||
      result?.groundingResult?.source ||
      'general';

    const citations = Array.isArray(result?.groundingResult?.citations)
      ? result.groundingResult.citations
      : [];

    const answerCandidates = [
      result?.groundingResult?.answer,
      result?.groundingResult?.response,
      result?.groundingResult?.chatbotResponse?.answer,
    ];
    const answer = answerCandidates.find(
      (candidate): candidate is string => typeof candidate === 'string' && candidate.trim().length > 0
    );

    const backendError =
      typeof result?.groundingResult?.error === 'string' && result.groundingResult.error.trim().length > 0
        ? result.groundingResult.error.trim()
        : undefined;

    if (answer) {
      return {
        answer,
        citations,
        groundingSource,
        backendError,
      };
    }

    if (backendError) {
      return {
        answer: `I encountered an upstream model error while processing your request: ${backendError}`,
        citations,
        groundingSource,
        backendError,
      };
    }

    return {
      answer: 'I apologize, but I could not generate a response.',
      citations,
      groundingSource,
    };
  }

  private getUserId(): string {
    const userInfo = authService.getUserInfo();
    return userInfo?.objectId || 'anonymous';
  }

  // Date-parsing helpers were extracted to ./dateParser.ts so they can be
  // unit-tested in isolation. Thin wrappers preserve the existing call-site
  // shape (`this.parseDateRange(...)` / `this.buildTemporalContext()`).
  private buildTemporalContext(): string {
    return buildTemporalContext();
  }
  private parseDateRange(
    query: string,
    intent: 'email' | 'calendar' | 'scheduling' | 'general' = 'general'
  ): { startDateTime: string; endDateTime: string; dateRangeDescription: string } {
    return parseDateRange(query, intent);
  }

  // A handler that swallowed a SignInExpiredError into its own generic text
  // must still show the sign-in message: the user's fix is to sign in again.
  private withSignInExpired(turn: ReauthTurn, response: GroundingResponse): GroundingResponse {
    return turn.signInExpired
      ? { ...response, success: false, answer: SIGN_IN_EXPIRED_MESSAGE, error: SIGN_IN_EXPIRED_MESSAGE, requiresSignIn: true }
      : response;
  }

  async classifyQuery(
    query: string,
    conversationHistory?: Array<{ role: string; content: string }>,
    activeContext?: ActiveContext,
    turn: ReauthTurn = new ReauthTurn()
  ): Promise<QueryClassification> {
    if (useNewIntentRouting()) {
      try {
        const accessToken = await authService.getAccessToken();
        if (!accessToken) throw new Error('No access token available');
        const intentDeployment = getIntentDeployment();
        const response = await fetch(`${GROUNDING_API_BASE}/queryintent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            query,
            conversationHistory: conversationHistory?.slice(-6) || [],
            ...(activeContext ? { activeContext } : {}),
            ...(intentDeployment ? { deployment: intentDeployment } : {}),
            // Apps this user's AD groups allow. The classifier is shown only
            // these, so it cannot route the user to an app they'd be bounced
            // from. Empty array => backend adds no routing block at all.
            availableApps: getAccessibleAppKeys(),
            // Per-request opt-in for the Building Score Card corpus. The backend
            // only adds property_query to the classifier's vocabulary when this
            // is true AND its own PROPERTY_CORPUS_ENABLED switch is on.
            ...(usePropertyCorpus() ? { propertyCorpus: true } : {}),
          }),
          signal: AbortSignal.timeout(8000),
        });

        if (response.ok) {
          const data = await response.json();
          if (data?.success && data?.classification) {
            logger.debug(`🤖 intent=${data.classification.intent} via ${data.model} in ${data.duration}ms`);
            return {
              ...data.classification,
              // Re-check against the current token rather than trusting the
              // echo. Groups can change between the request and the render,
              // and a suggestion is only ever a hint from the model.
              suggestedApp: sanitizeSuggestedApp(data.classification.suggestedApp),
            };
          }
        } else {
          logger.warn('QueryIntent returned', response.status, '- falling through to regex classifier');
        }
      } catch (err) {
        logger.warn('QueryIntent call failed, falling through to regex classifier:', err);
      }
    }

    // Regex fallback (also the production path until the feature flag flips).
    try {
      const classifierBody = JSON.stringify({
        query,
        userId: this.getUserId(),
        conversationHistory: conversationHistory?.slice(-6) || [],
      });
      const response = await fetchWithReauth(turn, '/queryclassifier', buildJsonHeaders, (headers) =>
        fetch(`${GROUNDING_API_BASE}/queryclassifier`, { method: 'POST', headers, body: classifierBody })
      );
      if (!response.ok) throw new Error(`Classification HTTP ${response.status}`);
      const data = await response.json();
      return data.classification || {
        groundingType: 'real_time_search',
        confidence: 0.5,
        reasons: ['Default classification'],
      };
    } catch (error) {
      // An expired sign-in must end the turn, not degrade to a different route.
      if (error instanceof SignInExpiredError) throw error;
      logger.warn('Regex classification failed, defaulting to real_time_search:', error);
      return {
        groundingType: 'real_time_search',
        confidence: 0.5,
        reasons: ['Classification service unavailable'],
      };
    }
  }

  async getGroundedResponse(
    query: string,
    conversationId?: string,
    conversationHistory?: Array<{ role: string; content: string }>,
    documentContents?: DocumentContextChunk[],
    activeContext?: ActiveContext,
    sessionDocumentIds?: string[],
    abortSignal?: AbortSignal
  ): Promise<GroundingResponse> {
    // Use crypto.randomUUID() when available so the queryId doubles as a
    // proper correlation id (Application Insights operation_Id) rather than
    // a guessable timestamp. Falls back to the legacy format on older browsers.
    const queryId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      ? `query_${crypto.randomUUID()}`
      : `query_${Date.now()}`;
    const userId = this.getUserId();
    const turn = new ReauthTurn();

    try {
      const recentHistory = conversationHistory?.slice(-10) || [];
      const hasSessionDocuments = Array.isArray(sessionDocumentIds) && sessionDocumentIds.length > 0;
      const comparisonQuery = queryLooksLikeDocumentComparison(query, activeContext);

      if (comparisonQuery && documentContents && documentContents.length > 0) {
        const representedDocuments = new Set(documentContents.map((doc) => doc.fileName));
        if (representedDocuments.size < 2) {
          const answer = [
            'I do not have enough retrieved context from both uploaded documents to compare them reliably.',
            'Please re-upload or select the two documents, then ask again.',
          ].join(' ');
          return {
            success: true,
            answer,
            citations: [],
            groundingSource: 'document_comparison_insufficient_context',
            queryId,
          };
        }
      }

      const structuredSpreadsheetResponse = buildStructuredSpreadsheetResponse(
        query,
        documentContents,
        activeContext,
        recentHistory
      );
      if (structuredSpreadsheetResponse) {
        return {
          success: true,
          answer: structuredSpreadsheetResponse.answer,
          citations: structuredSpreadsheetResponse.citations,
          groundingSource: structuredSpreadsheetResponse.groundingSource,
          queryId,
        };
      }

      // First, classify the query (with conversation history for follow-up detection)
      const classification = await this.classifyQuery(query, conversationHistory, activeContext, turn);
      logger.debug('Query classification:', classification);

      // App routing rides along on whatever answer this turn produces: the
      // handoff card is rendered next to the response, not instead of it.
      // classifyQuery has already filtered this to the user's own AD groups.
      const withSuggestedApp = (response: GroundingResponse): GroundingResponse =>
        classification.suggestedApp
          ? { ...response, suggestedApp: classification.suggestedApp }
          : response;

      // Handle Graph API queries (email, calendar, scheduling)
      if (classification.groundingType === 'email_query') {
        return withSuggestedApp(this.withSignInExpired(turn, await this.handleEmailQuery(query, queryId, recentHistory, classification.entities, activeContext, turn)));
      }
      if (classification.groundingType === 'calendar_query') {
        return withSuggestedApp(this.withSignInExpired(turn, await this.handleCalendarQuery(query, queryId, recentHistory, classification.entities, activeContext, turn)));
      }
      if (classification.groundingType === 'scheduling_query') {
        return withSuggestedApp(this.withSignInExpired(turn, await this.handleSchedulingQuery(query, queryId, recentHistory, classification.entities, activeContext, turn)));
      }

      // Handle content generation (no grounding needed)
      if (classification.groundingType === 'none') {
        logger.debug('✍️ Content generation query detected - using direct AI generation');
        // Fall through to enterprise grounding for AI generation
      }

      // If documents are provided, add only the highest-signal excerpts rather
      // than stuffing the entire uploaded file into the prompt.
      let enhancedQuery = query;
      const followUpClarification = buildDocumentFollowUpClarification(query, recentHistory, activeContext);
      if (followUpClarification) {
        enhancedQuery = `${query}\n\nFollow-up clarification:\n${followUpClarification}`;
        logger.debug('Added document follow-up clarification for ambiguous short query');
      }

      if (documentContents && documentContents.length > 0) {
        const hasFullDocumentContext = documentContents.some((doc) => doc.selectionMode === 'full_document');

        if (hasFullDocumentContext) {
          const fullDocuments = documentContents
            .filter((doc) => doc.selectionMode === 'full_document')
            .map((doc) => ({
              fileName: doc.fileName,
              content: doc.content || `[Document content: ${doc.fileName}]`,
            }));

          if (fullDocuments.length === 1 && documentContents.length === 1) {
            enhancedQuery = `${enhancedQuery}\n\nUploaded document content:\n${fullDocuments[0].content}`;
          } else {
            const docContext = documentContents.map((doc) => {
              const textContent = doc.content || `[Document content: ${doc.fileName}]`;
              if (doc.selectionMode === 'full_document') {
                return `\n\n--- Full document: ${doc.fileName} ---\n${textContent}`;
              }

              const scoreLabel = typeof doc.relevanceScore === 'number'
                ? ` | relevance ${doc.relevanceScore.toFixed(1)}`
                : '';
              return `\n\n--- Document excerpt: ${doc.fileName} | chunk ${doc.chunkIndex + 1}/${doc.totalChunks}${scoreLabel} ---\n${textContent}`;
            }).join('\n');

            enhancedQuery = `${enhancedQuery}\n\nUploaded document contents:${docContext}`;
          }
        } else {
          const docContext = documentContents.map(doc => {
            const textContent = doc.content || `[Document excerpt: ${doc.fileName}]`;
            const scoreLabel = typeof doc.relevanceScore === 'number'
              ? ` | relevance ${doc.relevanceScore.toFixed(1)}`
              : '';
            return `\n\n--- Document: ${doc.fileName} | chunk ${doc.chunkIndex + 1}/${doc.totalChunks}${scoreLabel} ---\n${textContent}`;
          }).join('\n');

          enhancedQuery = `${enhancedQuery}\n\nRelevant excerpts from uploaded documents:${docContext}`;
        }
        logger.debug('Enhanced query with document context:', {
          chunkCount: documentContents.length,
          hasFullDocumentContext,
          enhancedLength: enhancedQuery.length,
        });
      }

      const requestPayload = {
        queryId,
        query: enhancedQuery,
        userId,
        conversationId,
        classification,
        conversationHistory: recentHistory,
        ...(activeContext ? { activeContext } : {}),
        hasDocuments: (documentContents && documentContents.length > 0) || hasSessionDocuments,
        ...(hasSessionDocuments ? { documentIds: sessionDocumentIds } : {}),
        ...(getChatDeployment() ? { deployment: getChatDeployment() } : {}),
        // Second half of the property-corpus gate, asserted again on the
        // grounding call because it is a separate request to a slotless backend.
        ...(usePropertyCorpus() ? { propertyCorpus: true } : {})
      };

      const performEnterpriseGroundingRequest = async () => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Request-Id': queryId,
        };
        if (hasSessionDocuments) {
          // Session documents are per-user: a token failure must fail the turn.
          const accessToken = await authService.getAccessToken();
          if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
        } else {
          Object.assign(headers, await buildJsonHeaders());
        }

        const response = await fetchWithReauth(turn, '/enterprisegrounding', async () => headers, (sendHeaders) =>
          fetch(`${GROUNDING_API_BASE}/enterprisegrounding`, {
            method: 'POST',
            headers: sendHeaders,
            body: JSON.stringify(requestPayload),
            signal: abortSignal,
          })
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        return this.parseGroundingResult(result);
      };

      let { answer, citations, groundingSource, backendError } = await performEnterpriseGroundingRequest();
      if (backendError) {
        logger.warn('EnterpriseGrounding returned no answer payload:', backendError);
      }

      if (backendError && documentContents && documentContents.length > 0 && !abortSignal?.aborted) {
        logger.warn('Retrying document grounding after upstream model error');
        await new Promise((resolve) => setTimeout(resolve, 750));
        ({ answer, citations, groundingSource, backendError } = await performEnterpriseGroundingRequest());
      }

      return withSuggestedApp({
        success: true,
        answer,
        citations,
        groundingSource,
        queryId
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('Grounding service error:', error);
      
      const errorAnswer = error instanceof SignInExpiredError
        ? SIGN_IN_EXPIRED_MESSAGE
        : `I encountered an error processing your request: ${errorMessage}`;
      
      return {
        success: false,
        answer: errorAnswer,
        citations: [],
        groundingSource: 'error',
        queryId,
        error: errorMessage,
        ...(error instanceof SignInExpiredError ? { requiresSignIn: true } : {})
      };
    }
  }

  private async handleEmailQuery(
    query: string,
    queryId: string,
    conversationHistory: Array<{ role: string; content: string }>,
    entities?: any,
    activeContext?: ActiveContext,
    turn: ReauthTurn = new ReauthTurn()
  ): Promise<GroundingResponse> {
    try {
      logger.debug('📧 Handling email query:', query);

      // Use AI entities if available, otherwise fall back to regex parsing
      let filters: EmailFilterCriteria;
      if (entities && entities.people && entities.people.length > 0) {
        logger.debug('🤖 Using AI-extracted entities:', entities);
        filters = this.parseEmailFiltersWithEntities(query, entities);
      } else {
        logger.debug('🔄 Using regex-based entity extraction (AI entities not available)');
        filters = this.parseEmailFilters(query);
      }
      
      const wantsThreadSummary = this.shouldSummarizeThread(query);

      logger.debug('Parsed email filters:', {
        from: filters.from,
        dateRange: filters.startDate ? `${filters.startDate.toISOString()} to ${filters.endDate?.toISOString()}` : 'none',
        isUnread: filters.isUnread,
        searchText: filters.searchText || 'none'
      });

      // Determine how many emails to fetch based on the query type
      let emailLimit = 50; // Default
      if (filters.startDate || filters.endDate) {
        // For date range queries, server-side filtering will handle most of the work
        // We only need more emails for very broad date ranges
        const daysDiff = filters.startDate && filters.endDate 
          ? Math.ceil((filters.endDate.getTime() - filters.startDate.getTime()) / (1000 * 60 * 60 * 24))
          : 30; // Default to 30 days if only one date is specified
        
        if (daysDiff > 365) {
          emailLimit = 200; // For year-long queries
        } else if (daysDiff > 90) {
          emailLimit = 150; // For quarter-long queries
        } else if (daysDiff > 30) {
          emailLimit = 100; // For multi-month queries
        } else {
          emailLimit = 50; // For month-long queries (server-side filtering is efficient)
        }
      }

      // KQL mailbox-wide search when enabled; falls back to the legacy recency
      // list otherwise. KQL fixes the "only returns recent emails" problem because
      // results are relevance-ranked across the whole mailbox, not just the newest N.
      //
      // Three paths in preference order:
      //  1. Model-emitted KQL (entities.kql) — the intent classifier wrote the
      //     full KQL from the user's natural-language query. Highest fidelity.
      //  2. Entity-composed KQL — frontend synthesises from from/search/dates.
      //     Fallback when the model didn't emit a valid kql field.
      //  3. Legacy /me/messages recency list — when intent routing is off
      //     (prod hostnames) or no search signal at all.
      let emails: Email[];
      let count: number;
      const modelKql = typeof entities?.kql === 'string' && entities.kql.trim().length > 0
        ? entities.kql.trim()
        : null;

      if (useNewIntentRouting() && modelKql) {
        logger.debug(`🤖 Using model-emitted KQL: "${modelKql}"`);
        const result = await graphService.searchEmailsKql({
          kql: modelKql,
          size: 25,
          maxPages: 4,
        });
        emails = result.emails;
        count = result.count;
        logger.debug(`📧 KQL hits=${emails.length} totalReported=${result.totalReported} more=${result.moreAvailable}`);
      } else if (useNewIntentRouting() && (filters.searchText || filters.from || filters.startDate)) {
        const result = await graphService.searchEmailsKql({
          kql: filters.searchText,
          from: filters.from,
          startDate: filters.startDate?.toISOString().slice(0, 10),
          endDate: filters.endDate?.toISOString().slice(0, 10),
          size: 25,
          maxPages: 4,
        });
        emails = result.emails;
        count = result.count;
        logger.debug(`📧 KQL search hits=${emails.length} totalReported=${result.totalReported} more=${result.moreAvailable}`);
      } else {
        const result = await graphService.getEmails({
          top: emailLimit,
          unreadOnly: filters.isUnread,
          from: filters.from,
          startDateTime: filters.startDate?.toISOString(),
          endDateTime: filters.endDate?.toISOString(),
          search: filters.searchText,
          includeBody: wantsThreadSummary, // Only fetch body content if we need it for summarization
        });
        emails = result.emails;
        count = result.count;
      }

      logger.debug(`Fetching up to ${emailLimit} emails for date range query`);
      logger.debug(`Found ${emails.length} emails matching filters (${count} total)`);
      
      // Debug: Log the actual dates of the first few emails
      if (emails.length > 0) {
        logger.debug('Sample email dates received:');
        emails.slice(0, 5).forEach((email, index) => {
          logger.debug(`  ${index + 1}. ${email.subject} - ${email.receivedDateTime}`);
        });
      }

      let threadSummary: EmailThreadSummary | null = null;
      if (wantsThreadSummary && emails.length > 0) {
        const targetEmail = this.findThreadTargetEmail(query, emails);
        if (targetEmail?.conversationId) {
          logger.debug(`Generating summary for thread: ${targetEmail.conversationId}`);
          threadSummary = await this.summarizeEmailThread(targetEmail.conversationId);
          logger.debug('Generated thread summary:', {
            subject: threadSummary.subject,
            messageCount: threadSummary.messageCount,
            participants: threadSummary.participants.length
          });
        }
      }

      // Format emails for AI context
      const emailContext = graphService.formatEmailsForContext(emails);
      
      // Send to AI for natural language response. Temporal context includes today,
      // the current business week, and current quarter — helps the model correctly
      // interpret phrases like "this week" and "last quarter" in email context.
      let enhancedQuery = `${query}\n\n${this.buildTemporalContext()}\n\nHere are the user's emails:\n${emailContext}`;

      if (threadSummary) {
        enhancedQuery += `\n\nThread Summary:\nSubject: ${threadSummary.subject}\nParticipants: ${threadSummary.participants.join(', ')}\nMessages: ${threadSummary.messageCount}\nSummary: ${threadSummary.summary}${threadSummary.keyPoints ? '\nKey Points: ' + threadSummary.keyPoints.join(', ') : ''}${threadSummary.sentiment ? '\nSentiment: ' + threadSummary.sentiment : ''}${threadSummary.requiresAction ? '\n⚠️ Action Required' : ''}`;
      }
      
      const response = await fetchWithReauth(turn, '/enterprisegrounding', buildJsonHeaders, (headers) => fetch(`${GROUNDING_API_BASE}/enterprisegrounding`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          queryId,
          query: enhancedQuery,
          userId: this.getUserId(),
          classification: {
            intent: 'email_query',
            groundingType: 'none',
            confidence: 1,
            reasons: ['Email data provided'],
          },
          conversationHistory,
          ...(activeContext ? { activeContext } : {}),
          ...(getChatDeployment() ? { deployment: getChatDeployment() } : {})
        })
      }));

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      let answer = result.groundingResult?.answer || result.groundingResult?.response || 'Here are your emails.';

      if (threadSummary) {
        answer = `${answer}\n\n**Thread Summary**\n**Subject:** ${threadSummary.subject}\n**Participants:** ${threadSummary.participants.join(', ')}\n**Messages:** ${threadSummary.messageCount}\n**Summary:** ${threadSummary.summary}${threadSummary.keyPoints ? '\n**Key Points:** ' + threadSummary.keyPoints.join(', ') : ''}${threadSummary.sentiment ? '\n**Sentiment:** ' + threadSummary.sentiment : ''}${threadSummary.requiresAction ? '\n⚠️ **Action Required**' : ''}`;
      }
      
      return {
        success: true,
        answer,
        citations: [],
        groundingSource: 'email_analysis',
        queryId,
        metadata: {
          filterCriteria: filters,
          resultCount: emails.length,
          totalCount: count
        }
      };
    } catch (error) {
      logger.error('Error processing email query:', error);
      return {
        success: false,
        answer: 'I encountered an error processing your email request.',
        citations: [],
        groundingSource: 'email_analysis',
        queryId,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private shouldSummarizeThread(query: string): boolean {
    return /thread|conversation|chain/i.test(query) || /entire (email|conversation)/i.test(query) || /summarize .*outlook/i.test(query);
  }

  private findThreadTargetEmail(query: string, emails: Email[]): Email | null {
    if (emails.length === 0) return null;
    const queryLower = query.toLowerCase();

    const subjectMatch = emails.find(email =>
      email.subject && queryLower.includes(email.subject.toLowerCase())
    );

    return subjectMatch || emails[0];
  }

  
  private async handleCalendarQuery(
    query: string,
    queryId: string,
    conversationHistory: Array<{ role: string; content: string }>,
    entities?: any,
    activeContext?: ActiveContext,
    turn: ReauthTurn = new ReauthTurn()
  ): Promise<GroundingResponse> {
    try {
      logger.debug('📅 Handling calendar query:', query);
      
      // Prefer AI-extracted dates when the intent classifier returned a concrete
      // start date. Only fall back to regex parsing when the AI has no structured
      // dates — and in that case bias the default to "this business week" for
      // calendar queries so "summarize my schedule for the week" does not silently
      // resolve to "past 7 days."
      let dateRange;
      if (entities?.dates?.start) {
        const s = new Date(entities.dates.start);
        const e = entities.dates.end ? new Date(entities.dates.end) : new Date(s);
        if (!entities.dates.end) e.setHours(23, 59, 59, 999);
        dateRange = {
          startDateTime: s.toISOString(),
          endDateTime: e.toISOString(),
          dateRangeDescription:
            entities.dates.relative ||
            (entities.dates.end ? `${entities.dates.start} to ${entities.dates.end}` : entities.dates.start),
        };
        logger.debug('🤖 Using AI-extracted dates:', dateRange);
      } else {
        logger.debug('🔄 Using regex-based date parsing (no AI dates, calendar-intent default)');
        dateRange = this.parseDateRange(query, 'calendar');
      }

      const { startDateTime, endDateTime, dateRangeDescription } = dateRange;
      
      logger.debug(`Calendar date range: ${dateRangeDescription}`);
      
      // Fetch calendar events from Graph API
      const { events } = await graphService.getCalendarEvents({
        startDateTime,
        endDateTime
      });
      
      // Format events for AI context
      const calendarContext = graphService.formatEventsForContext(events);
      
      // Send to AI for natural language response. Temporal context includes today,
      // the current business-week bounds, and current quarter so the model can
      // resolve ambiguous phrases like "the week" without guessing.
      const enhancedQuery = `${query}\n\n${this.buildTemporalContext()}\nShowing events for ${dateRangeDescription}.\n\nHere are the user's calendar events:\n${calendarContext}`;
      
      const response = await fetchWithReauth(turn, '/enterprisegrounding', buildJsonHeaders, (headers) => fetch(`${GROUNDING_API_BASE}/enterprisegrounding`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          queryId,
          query: enhancedQuery,
          userId: this.getUserId(),
          classification: {
            intent: 'calendar_query',
            groundingType: 'none',
            confidence: 1,
            reasons: ['Calendar data provided'],
          },
          conversationHistory,
          ...(activeContext ? { activeContext } : {}),
          ...(getChatDeployment() ? { deployment: getChatDeployment() } : {})
        })
      }));

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      const answer = result.groundingResult?.answer || result.groundingResult?.response || 'Here is your calendar.';

      return {
        success: true,
        answer,
        citations: [],
        groundingSource: 'calendar_analysis',
        queryId
      };
    } catch (error) {
      logger.error('Calendar query error:', error);
      const errorAnswer = 'I was unable to access your calendar. Please make sure you have granted calendar permissions.';
      
      return {
        success: false,
        answer: errorAnswer,
        citations: [],
        groundingSource: 'calendar_analysis',
        queryId,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private async handleSchedulingQuery(
    query: string,
    queryId: string,
    conversationHistory: Array<{ role: string; content: string }>,
    entities?: any,
    activeContext?: ActiveContext,
    turn: ReauthTurn = new ReauthTurn()
  ): Promise<GroundingResponse> {
    try {
      logger.debug('🗓️ Handling scheduling query:', query);
      
      // Define regex patterns for fallback and history parsing
      const emailPattern = /[\w.-]+@[\w.-]+\.\w+/g;
      const namePattern = /\b([A-Z][a-z]+\s+[A-Z][a-z]+)\b/g;
      
      // Use AI entities if available, otherwise fall back to regex parsing
      let emailsInQuery: string[] = [];
      let potentialNames: string[] = [];
      
      if (entities && entities.people && entities.people.length > 0) {
        logger.debug('🤖 Using AI-extracted people entities:', entities.people);
        potentialNames = entities.people;
      } else {
        logger.debug('🔄 Using regex-based entity extraction (AI entities not available)');
        // Extract email addresses from query (simple pattern matching)
        emailsInQuery = query.match(emailPattern) || [];
        
        // Extract potential names - look for "First Last" patterns
        const twoWordNames = query.match(namePattern) || [];
        potentialNames = twoWordNames;
      }
      
      // Combine names
      const allPotentialNames = [...potentialNames];
      
      // Filter out common words that aren't names
      const excludeWords = ['When', 'What', 'Where', 'How', 'Can', 'Could', 'Would', 'Should', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'Today', 'Tomorrow', 'Next', 'This', 'Meet', 'Meeting', 'Schedule', 'Find', 'Available', 'Window', 'Time', 'Times', 'Week', 'Month', 'Year', 'Morning', 'Afternoon', 'Evening', 'Based On', 'Let Me', 'Available Window'];
      const filteredNames = allPotentialNames.filter((name: string) => {
        const words = name.split(' ');
        return !words.some((word: string) => excludeWords.includes(word));
      });
      
      logger.debug('Detected names in query:', filteredNames);
      
      // For follow-up queries, extract names/emails from conversation history
      let contextEmails: string[] = [];
      let contextNames: string[] = [];
      if (filteredNames.length === 0 && emailsInQuery.length === 0 && conversationHistory.length > 0) {
        logger.debug('No names/emails in query, checking conversation history for context...');
        for (const msg of conversationHistory.slice(-6)) {
          const content = msg.content || '';
          // Extract emails from history
          const historyEmails = content.match(emailPattern) || [];
          contextEmails.push(...historyEmails);
          // Extract names from history
          const historyNames = content.match(namePattern) || [];
          const filteredHistoryNames = historyNames.filter(name => {
            const words = name.split(' ');
            return !words.some(word => excludeWords.includes(word));
          });
          contextNames.push(...filteredHistoryNames);
        }
        // Deduplicate
        contextEmails = [...new Set(contextEmails)];
        contextNames = [...new Set(contextNames)];
        logger.debug('Found context from history - emails:', contextEmails, 'names:', contextNames);
      }
      
      // Resolve names to emails (from query or context)
      const namesToResolve = filteredNames.length > 0 ? filteredNames : contextNames;
      const resolvedEmails: string[] = [...emailsInQuery, ...contextEmails];
      const resolvedNames: string[] = [];
      
      for (const name of namesToResolve) {
        const email = await graphService.resolveNameToEmail(name);
        if (email && !resolvedEmails.includes(email)) {
          resolvedEmails.push(email);
          resolvedNames.push(`${name} (${email})`);
          logger.debug(`Resolved "${name}" to ${email}`);
        }
      }
      
      // Deduplicate final emails
      const uniqueEmails = [...new Set(resolvedEmails)];
      
      if (uniqueEmails.length === 0) {
        const noEmailsAnswer = 'To find available meeting times, please include the names or email addresses of the people you want to meet with. For example: "When can John Smith and Sarah Jones meet next week?" or "When can john@company.com meet?"';
        return {
          success: true,
          answer: noEmailsAnswer,
          citations: [],
          groundingSource: 'scheduling_analysis',
          queryId
        };
      }
      
      // Use AI-extracted dates when available; otherwise fall back to regex with
      // scheduling-intent (default = next 14 days). Old manual past-7-days override
      // hack is no longer needed because the default already looks forward.
      let startDateTime: string;
      let endDateTime: string;
      let dateRangeDescription: string;
      if (entities?.dates?.start) {
        const s = new Date(entities.dates.start);
        const e = entities.dates.end
          ? new Date(entities.dates.end)
          : new Date(s.getTime() + 14 * 24 * 60 * 60 * 1000);
        startDateTime = s.toISOString();
        endDateTime = e.toISOString();
        dateRangeDescription = entities.dates.relative || 'the specified range';
      } else {
        ({ startDateTime, endDateTime, dateRangeDescription } = this.parseDateRange(query, 'scheduling'));
      }
      
      logger.debug(`Scheduling date range: ${dateRangeDescription}`);
      
      // Find meeting times using Graph API
      const { suggestions, emptySuggestionsReason } = await graphService.findMeetingTimes({
        attendees: uniqueEmails,
        durationMinutes: 60,
        startDateTime,
        endDateTime
      });
      
      // Build attendee description (use resolved names if available, or unique emails)
      const attendeeDescription = resolvedNames.length > 0 
        ? resolvedNames.join(', ')
        : uniqueEmails.join(', ');
      
      if (suggestions.length === 0) {
        const noTimesAnswer = emptySuggestionsReason 
          ? `I couldn't find any available times for ${attendeeDescription} during ${dateRangeDescription}: ${emptySuggestionsReason}`
          : `I couldn't find any times when ${attendeeDescription} are all available during ${dateRangeDescription}.`;
        return {
          success: true,
          answer: noTimesAnswer,
          citations: [],
          groundingSource: 'scheduling_analysis',
          queryId
        };
      }
      
      // Format suggestions for AI context
      const suggestionsContext = graphService.formatSuggestionsForContext(suggestions);

      // Send to AI for natural language response with rich temporal context so
      // the model correctly frames relative ranges in its answer.
      const enhancedQuery = `${query}\n\n${this.buildTemporalContext()}\nThe search range is ${dateRangeDescription}.\n\nHere are the available meeting times for ${attendeeDescription}:\n${suggestionsContext}`;
      
      const response = await fetchWithReauth(turn, '/enterprisegrounding', buildJsonHeaders, (headers) => fetch(`${GROUNDING_API_BASE}/enterprisegrounding`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          queryId,
          query: enhancedQuery,
          userId: this.getUserId(),
          classification: {
            intent: 'scheduling_query',
            groundingType: 'none',
            confidence: 1,
            reasons: ['Scheduling data provided'],
          },
          ...(getChatDeployment() ? { deployment: getChatDeployment() } : {}),
          conversationHistory,
          ...(activeContext ? { activeContext } : {})
        })
      }));

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();

      const answer = result.groundingResult?.answer || result.groundingResult?.response || 'Here are the available meeting times.';

      return {
        success: true,
        answer,
        citations: [],
        groundingSource: 'scheduling_analysis',
        queryId
      };
    } catch (error) {
      logger.error('Scheduling query error:', error);
      const errorAnswer = 'I was unable to find meeting times. Please make sure you have granted calendar permissions.';
      
      return {
        success: false,
        answer: errorAnswer,
        citations: [],
        groundingSource: 'scheduling_analysis',
        queryId,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  private parseEmailFiltersWithEntities(query: string, entities: any): EmailFilterCriteria {
    const filters: EmailFilterCriteria = {};

    // Use AI-extracted entities first
    if (entities.people && entities.people.length > 0) {
      filters.from = entities.people[0]; // Use first person extracted by AI
      logger.debug(`🤖 AI extracted person: "${filters.from}"`);
    }

    // Use AI-extracted keywords for search if available. Filter out both the
    // action verbs and the meta-words that describe "email" itself — those are
    // part of the user's question framing, not content that appears in a real
    // email body. Then quote multi-word keywords individually so KQL sees each
    // as its own phrase (joined = AND).
    if (entities.keywords && entities.keywords.length > 0) {
      const DROP = new Set([
        'summarize', 'find', 'search', 'show', 'get', 'list', 'read',
        'email', 'emails', 'e-mail', 'mail', 'message', 'messages', 'inbox',
      ]);
      const searchKeywords = (entities.keywords as string[])
        .filter((k) => k && !DROP.has(k.toLowerCase()));
      if (searchKeywords.length > 0) {
        filters.searchText = searchKeywords
          .map((k) => (/\s/.test(k) ? `"${k.replace(/"/g, '')}"` : k))
          .join(' ');
        logger.debug(`🤖 AI extracted search terms: "${filters.searchText}"`);
      }
    }

    // Only apply a date range if the AI classifier extracted one OR the query
    // has explicit date keywords. Previously we unconditionally called
    // parseDateRange which falls back to "past 7 days" for any unmatched
    // query — that silently narrowed mailbox-wide topic/sender searches to
    // the past week, causing the "No emails found" bug on Caleb/Insight
    // Manager queries.
    const aiDates = entities?.dates;
    if (aiDates?.start) {
      filters.startDate = new Date(aiDates.start);
      if (aiDates.end) filters.endDate = new Date(aiDates.end);
    } else if (queryHasExplicitDate(query)) {
      const dateRange = this.parseDateRange(query, 'email');
      filters.startDate = new Date(dateRange.startDateTime);
      filters.endDate = new Date(dateRange.endDateTime);
    }

    return filters;
  }

  private parseEmailFilters(query: string): EmailFilterCriteria {
    const filters: EmailFilterCriteria = {};

    // Only constrain by date if the user said something date-ish. Falling back
    // to "past 7 days" for every unmatched query was silently excluding older
    // emails on topic/sender searches and returning "No emails found."
    if (queryHasExplicitDate(query)) {
      const dateRange = this.parseDateRange(query, 'email');
      filters.startDate = new Date(dateRange.startDateTime);
      filters.endDate = new Date(dateRange.endDateTime);
    }

    // Extract email addresses first
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi;
    const emails = query.match(emailRegex) || [];
    if (emails.length > 0) {
      filters.from = emails[0];
    } else {
      // Extract sender name from patterns like "from John Doe", "emails from Jane", "by Bob Smith" (with common typos)
      const senderPatterns = [
        /\bemails?\s+(?:from|form|by)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/gi,
        /\b(?:from|form|by)\s+([A-Z][a-z]+\s+[A-Z][a-z]+)/gi,
        /\bemails?\s+(?:from|form|by)\s+([a-zA-Z]+\s+[a-zA-Z]+)/gi,
        /\b(?:from|form|by)\s+([a-zA-Z]+\s+[a-zA-Z]+)/gi
      ];
      
      for (const pattern of senderPatterns) {
        const match = query.match(pattern);
        logger.debug(`Testing pattern ${pattern} on query "${query}":`, match);
        if (match && match[1]) {
          filters.from = match[1];
          logger.debug(`Extracted sender name: "${filters.from}"`);
          break;
        }
      }
      
      // If no pattern matched, try the scheduling query approach
      if (!filters.from) {
        // Match sequences of capitalized words (e.g., "Jenny Livingston")
        const namePattern = /\b([A-Z][a-z]+\s+[A-Z][a-z]+)\b/g;
        const twoWordNames = query.match(namePattern) || [];
        
        // Filter out common words that aren't names
        const excludeWords = ['When', 'What', 'Where', 'How', 'Can', 'Could', 'Would', 'Should', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December', 'Today', 'Tomorrow', 'Next', 'This', 'Meet', 'Meeting', 'Schedule', 'Find', 'Available', 'Window', 'Time', 'Times', 'Week', 'Month', 'Year', 'Morning', 'Afternoon', 'Evening', 'Based', 'Let', 'Available', 'Window', 'Email', 'Emails', 'From'];
        const filteredNames = twoWordNames.filter(name => {
          const words = name.split(' ');
          return !words.some(word => excludeWords.includes(word));
        });
        
        logger.debug('Detected capitalized names in query:', filteredNames);
        
        if (filteredNames.length > 0) {
          filters.from = filteredNames[0];
          logger.debug(`Using capitalized name: "${filters.from}"`);
        }
      }
      
      if (!filters.from) {
        logger.debug(`No sender name extracted from query: "${query}"`);
      }
    }

    filters.isUnread = /\b(unread|new)\b/i.test(query);
    // Simplified: only focus on dates and people filtering

    // Only extract search terms if this is not primarily a date/filter query
    const hasDateFilter = /\b(last year|last month|this month|next month|yesterday|today|tomorrow|week|month|year|\d{1,2}\/\d{1,2}|\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\b|in\s+\d{4})/i.test(query);
    const hasFilterWords = /\b(summarize|overview|emails?|from|to|subject|date|between|before|after)/i.test(query);
    
    // Extract search terms for keyword searches
    let searchTerms: string[] = [];
    
    // Pattern 1: "emails about [topic]" or "emails containing [keyword]"
    const keywordMatch = query.match(/\b(about|regarding|concerning|with|containing|mentions|referring to|related to|on|involving)\s+(.+?)(?:\s+(?:from|to|in|during|at|on|by|$))/i);
    if (keywordMatch) {
      searchTerms = keywordMatch[2]
        .split(/\s+/)
        .filter(term => term.length > 2)
        .slice(0, 3); // Limit to 3 keywords
    }
    // Pattern 2: "emails [topic]" when no other filters present
    else if (!hasDateFilter && !hasFilterWords && !filters.from) {
      searchTerms = query
        .replace(/\b(show|find|get|search|look for|emails?)\s+/gi, '')
        .trim()
        .split(/\s+/)
        .filter(term => term.length > 2)
        .slice(0, 3); // Limit to 3 keywords
    }
    // Pattern 3: Pure content search without filter words
    else if (!hasDateFilter && !hasFilterWords) {
      searchTerms = query
        .replace(/(unread|new|attachment|attached|file|important|urgent|priority|emails?|show|find|get|search|look for)/gi, '')
        .trim()
        .split(/\s+/)
        .filter(term => term.length > 2)
        .slice(0, 3); // Limit to 3 keywords
    }

    if (searchTerms.length > 0) {
      filters.searchText = searchTerms.join(' ');
      logger.debug(`Extracted search keywords: "${filters.searchText}"`);
    } else {
      logger.debug(`No search keywords extracted from query: "${query}"`);
    }

    return filters;
  }

  private async summarizeEmailThread(threadId: string): Promise<EmailThreadSummary> {
    try {
      const { thread } = await graphService.getEmailThread(threadId);
      if (!thread?.length) {
        throw new Error('No messages in thread');
      }

      const participants = new Set<string>();
      let lastMessageDate = new Date(0);
      let requiresAction = false;

      thread.forEach(msg => {
        if (msg.from) {
          participants.add(msg.from);
        }
        const msgDate = new Date(msg.receivedDateTime);
        if (msgDate > lastMessageDate) {
          lastMessageDate = msgDate;
        }
        
        // Check for action items in subject, body preview, or body content
        const contentToCheck = msg.subject + ' ' + (msg.bodyPreview || '') + ' ' + (msg.body?.content || '');
        if (msg.importance === 'high' ||
            /\b(urgent|action|required|follow up|needs attention)\b/i.test(contentToCheck)) {
          requiresAction = true;
        }
      });

      const summary = `Conversation about "${thread[0].subject}" with ${participants.size} participants and ${thread.length} messages`;

      let sentiment: 'positive' | 'neutral' | 'negative' = 'neutral';
      const positiveWords = ['thanks', 'thank you', 'great', 'good', 'excellent', 'appreciate'];
      const negativeWords = ['problem', 'issue', 'urgent', 'failed', 'error', 'sorry'];

      // Analyze sentiment from subject, body preview, and body content
      const content = thread.map(m => {
        let msgContent = m.subject + ' ' + (m.bodyPreview || '');
        if (m.body?.content) {
          // Strip HTML from body content if present
          msgContent += ' ' + m.body.content.replace(/<[^>]*>/g, ' ');
        }
        return msgContent;
      }).join(' ').toLowerCase();

      const positiveCount = positiveWords.filter(w => content.includes(w)).length;
      const negativeCount = negativeWords.filter(w => content.includes(w)).length;

      if (positiveCount > negativeCount + 2) sentiment = 'positive';
      else if (negativeCount > positiveCount + 2) sentiment = 'negative';

      const keyPoints: string[] = [];
      if (requiresAction) {
        keyPoints.push('Action required');
      }
      if (thread.some(m => m.hasAttachments)) {
        keyPoints.push('Contains attachments');
      }

      return {
        id: threadId,
        subject: thread[0].subject,
        participants: Array.from(participants),
        messageCount: thread.length,
        lastMessageDate,
        summary,
        keyPoints: keyPoints.length ? keyPoints : undefined,
        sentiment,
        requiresAction
      };
    } catch (error) {
      logger.error('Error summarizing thread:', error);
      throw error;
    }
  }

  }

export const groundingService = new GroundingService();
